import { useEffect, useState } from "react";
import { Loader2, MonitorSmartphone } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AddressInput } from "@/components/AddressInput";

export const CONDITIONS = [["new", "New"], ["excellent", "Excellent"], ["good", "Good"], ["fair", "Fair"]];
export const STATUSES = [["in_stock", "In stock"], ["sold", "Sold"], ["leased", "Leased"]];
const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";
const EMPTY = { brand: "", model: "", condition: "new", serial_number: "", status: "in_stock", customer_name: "", company_id: "", location_address: "", address_verified: false, date: "", lease_end: "", price: "", notes: "" };

function F({ label, children, className = "" }) {
  return <label className={`block ${className}`}><span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">{label}</span>{children}</label>;
}

/** Add / edit a device. `asset` null = create. */
export function AssetDialog({ asset, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  useEffect(() => {
    api.get("/platform/assets/customers").then((r) => setCustomers(r.data)).catch(() => {});
    setF(asset ? { ...EMPTY, ...Object.fromEntries(Object.entries(asset).filter(([k]) => k in EMPTY).map(([k, v]) => [k, v ?? ""])) } : EMPTY);
  }, [asset]);

  const pickCustomer = (e) => {
    const c = customers.find((x) => x.id === e.target.value);
    setF((x) => ({ ...x, company_id: e.target.value, customer_name: c && !x.customer_name ? c.name : x.customer_name,
      location_address: c && !x.location_address ? c.address || "" : x.location_address, address_verified: c && !x.location_address ? false : x.address_verified }));
  };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    const body = { ...f, company_id: f.company_id || null, date: f.date || null, lease_end: f.status === "leased" && f.lease_end ? f.lease_end : null, price: f.price === "" ? null : Number(f.price) };
    try {
      const { data } = asset?.id ? await api.put(`/platform/assets/${asset.id}`, body) : await api.post("/platform/assets", body);
      toast.success(asset?.id ? `${data.asset_number} updated` : `Device registered as ${data.asset_number}`);
      onSaved(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const out = f.status !== "in_stock";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="asset-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><MonitorSmartphone className="h-5 w-5 text-primary" /> {asset?.id ? `Edit ${asset.asset_number}` : "Register a device"}</DialogTitle>
          <DialogDescription>{asset?.id ? "Update the device record." : "The asset number is assigned automatically when you save."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 pt-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Brand"><input required value={f.brand} onChange={set("brand")} placeholder="Apple / Samsung / Lenovo" data-testid="asset-brand" className={inputCls} /></F>
            <F label="Model"><input required value={f.model} onChange={set("model")} placeholder='iPad 10th gen 64GB' data-testid="asset-model" className={inputCls} /></F>
            <F label="Serial number"><input required value={f.serial_number} onChange={set("serial_number")} data-testid="asset-serial" className={`${inputCls} font-mono`} /></F>
            <F label="Condition">
              <select value={f.condition} onChange={set("condition")} data-testid="asset-condition" className={inputCls}>{CONDITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            </F>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3">
            <F label="Status">
              <div className="flex gap-2" data-testid="asset-status">
                {STATUSES.map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setF((x) => ({ ...x, status: v }))} data-testid={`asset-status-${v}`}
                    className={`h-9 flex-1 rounded-lg text-sm font-medium transition ${f.status === v ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"}`}>{l}</button>
                ))}
              </div>
            </F>
            {out && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <F label="Customer account (optional)">
                    <select value={f.company_id} onChange={pickCustomer} data-testid="asset-company" className={inputCls}>
                      <option value="">— Not a Logerd account —</option>
                      {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </F>
                  <F label="Customer name"><input required value={f.customer_name} onChange={set("customer_name")} placeholder="Who has the device" data-testid="asset-customer" className={inputCls} /></F>
                </div>
                <F label="Location (address)">
                  <AddressInput value={f.location_address} verified={f.address_verified} onChange={(v, ok) => setF((x) => ({ ...x, location_address: v, address_verified: ok }))} testid="asset-address" className={inputCls} placeholder="Where the device is installed" />
                </F>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <F label={f.status === "leased" ? "Lease start" : "Sale date"}><input type="date" required value={f.date} onChange={set("date")} data-testid="asset-date" className={inputCls} /></F>
                  {f.status === "leased" && <F label="Lease end"><input type="date" value={f.lease_end} onChange={set("lease_end")} min={f.date || undefined} data-testid="asset-lease-end" className={inputCls} /></F>}
                  <F label={f.status === "leased" ? "Monthly lease ($)" : "Sale price ($)"}><input type="number" min="0" step="0.01" value={f.price} onChange={set("price")} data-testid="asset-price" className={inputCls} placeholder="0.00" /></F>
                </div>
              </>
            )}
          </div>
          <F label="Notes (optional)"><textarea value={f.notes} onChange={set("notes")} rows={2} data-testid="asset-notes" className={`${inputCls} h-auto py-2`} placeholder="Accessories, damage, invoice #…" /></F>
          <DialogFooter>
            <button type="submit" disabled={saving} data-testid="asset-save-btn" className="h-10 rounded-lg bg-primary px-5 text-sm font-medium text-white inline-flex items-center gap-2 hover:shadow disabled:opacity-60 transition">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {asset?.id ? "Save changes" : "Register device"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
